import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { useSecondaryPanel } from '@src/components/SecondaryPanel/SecondaryPanel';
import type { CommonReduxState } from '@src/models/commons/reduxState.interface';
import {
  SHIFT_LIST_FILTERS_STORAGE_KEY,
  SHIFT_LIST_PAGE_SIZE,
  SHIFT_LIST_SEARCH_IDS_SIZE,
  SHIFT_LIST_SORT_STORAGE_KEY,
  SHIFT_LIST_VIEW_STORAGE_KEY,
} from '@src/packages/shifts/constants';
import type { ShiftListItem } from '@src/packages/shifts/types';
import {
  filterValuesToShiftFilters,
  INIT_SHIFT_LIST_FILTERS,
} from '@src/packages/shifts/shiftsListFilterConfig';
import {
  isFilterPanelHydrated,
  useFirestoreRealtimeBatchedPagination,
  usePaginationWithFirestore,
  usePersistedFilters,
} from '@src/packages/utils';
import { migrateLegacyListViewState } from '@src/packages/utils/sessionStorage/viewState';
import ShiftService from '@src/services/ShiftService';

import { useShiftContext } from '../context';
import { buildShiftQueries } from '../utils/buildShiftQueries';
import {
  hasPersistedBootstrapShiftFilters,
  hasElasticShiftFilters,
  shouldCompletePersistedListFiltersBootstrap,
  shouldUsePersistedListFiltersBootstrap,
} from '../utils/shiftListFilterHelpers';
import { useShiftPoliciesSubcollections } from './useShiftPoliciesSubcollections';

export const useShiftsList = () => {
  const orgId = useSelector(
    ({ organizations }: CommonReduxState) => organizations.organization.id,
  );
  const { refresh } = useShiftContext();

  useState(() => {
    migrateLegacyListViewState(SHIFT_LIST_VIEW_STORAGE_KEY, {
      filters: SHIFT_LIST_FILTERS_STORAGE_KEY,
      sort: SHIFT_LIST_SORT_STORAGE_KEY,
    });
    return true;
  });

  const {
    values: filters,
    debouncedValues,
    setValue: setFilters,
    toggle,
    isOpen,
  } = useSecondaryPanel({ debounceDelay: 500 });

  const { initialFilters } = usePersistedFilters({
    viewStorageKey: SHIFT_LIST_VIEW_STORAGE_KEY,
    defaults: INIT_SHIFT_LIST_FILTERS,
    values: filters,
  });

  const persistedListFilters = useMemo(
    () => filterValuesToShiftFilters(initialFilters),
    [initialFilters],
  );

  const debouncedListFilters = useMemo(
    () => filterValuesToShiftFilters(debouncedValues),
    [debouncedValues],
  );

  const currentListFilters = useMemo(
    () => filterValuesToShiftFilters(filters),
    [filters],
  );

  const [isPersistedBootstrapComplete, setIsPersistedBootstrapComplete] = useState(() =>
    !hasPersistedBootstrapShiftFilters(persistedListFilters),
  );

  useEffect(() => {
    if (isPersistedBootstrapComplete) {
      return;
    }

    if (
      shouldCompletePersistedListFiltersBootstrap(
        debouncedListFilters,
        persistedListFilters,
        currentListFilters,
      )
    ) {
      setIsPersistedBootstrapComplete(true);
    }
  }, [
    isPersistedBootstrapComplete,
    debouncedListFilters,
    persistedListFilters,
    currentListFilters,
  ]);

  const shouldUsePersistedBootstrap = shouldUsePersistedListFiltersBootstrap(
    debouncedListFilters,
    persistedListFilters,
    isPersistedBootstrapComplete,
  );

  // Until the panel hydrates, the mount snapshot is the only meaningful filter
  // set: switching to the empty panel values would look like a filter change and
  // would drop the restored page.
  const listFilters =
    shouldUsePersistedBootstrap || !isFilterPanelHydrated(debouncedValues)
      ? persistedListFilters
      : debouncedListFilters;

  const [searchIds, setSearchIds] = useState<string[] | null>(null);
  const [searchTruncation, setSearchTruncation] = useState<{
    shown: number;
    total: number;
  } | null>(null);
  const [searchLoading, setSearchLoading] = useState(() =>
    hasElasticShiftFilters(persistedListFilters),
  );
  const [searchError, setSearchError] = useState<unknown>(null);
  const [searchNonce, setSearchNonce] = useState(0);

  const shiftServiceRef = useRef<ShiftService | null>(null);
  if (shiftServiceRef.current === null) {
    shiftServiceRef.current = new ShiftService();
  }

  const searchTerm = listFilters.searchTerm.trim();
  const hasElasticSearchFilters = hasElasticShiftFilters({
    searchTerm,
    shiftType: listFilters.shiftType,
    exceptional: listFilters.exceptional,
  });

  const isElasticResolutionPending =
    hasElasticSearchFilters && (searchLoading || searchIds == null);

  const refetchSearchIds = useCallback(() => {
    setSearchNonce(nonce => nonce + 1);
  }, []);

  useEffect(() => {
    refetchSearchIds();
  }, [refresh, refetchSearchIds]);

  useEffect(() => {
    if (!hasElasticSearchFilters) {
      setSearchIds(null);
      setSearchTruncation(null);
      setSearchLoading(false);
      setSearchError(null);
      return undefined;
    }

    if (!orgId) {
      setSearchIds([]);
      setSearchTruncation(null);
      setSearchLoading(false);
      setSearchError(null);
      return undefined;
    }

    let cancelled = false;
    setSearchLoading(true);
    setSearchIds(null);
    setSearchError(null);

    (async () => {
      try {
        const result = await shiftServiceRef.current?.searchIds({
          orgId,
          pageSize: SHIFT_LIST_SEARCH_IDS_SIZE,
          from: 0,
          searchTerm,
          shiftType: listFilters.shiftType,
          exceptional: listFilters.exceptional,
        });
        if (cancelled) return;
        const ids = result?.ids ?? [];
        const total = result?.total ?? ids.length;
        setSearchIds(ids);
        setSearchTruncation(total > ids.length ? { shown: ids.length, total } : null);
      } catch (error) {
        if (cancelled) return;
        setSearchIds([]);
        setSearchTruncation(null);
        setSearchError(error);
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    orgId,
    searchTerm,
    hasElasticSearchFilters,
    searchNonce,
    listFilters.shiftType,
    listFilters.exceptional,
  ]);

  const queriesList = useMemo(() => {
    if (!orgId) {
      return [];
    }
    if (hasElasticSearchFilters && (searchLoading || searchIds == null)) {
      return [];
    }
    return buildShiftQueries(
      orgId,
      hasElasticSearchFilters ? searchIds : null,
      listFilters,
    );
  }, [orgId, hasElasticSearchFilters, searchIds, searchLoading, listFilters]);

  const {
    data: realtimeShifts,
    loading: realtimeLoading,
    error: realtimeError,
    next,
    gotNewData,
  } = useFirestoreRealtimeBatchedPagination(
    queriesList,
    [
      orgId,
      searchIds,
      hasElasticSearchFilters,
      searchNonce,
      listFilters.shiftType,
      listFilters.status,
      listFilters.selectedDays,
      listFilters.exceptional,
      searchTerm,
      refresh,
    ],
    SHIFT_LIST_PAGE_SIZE,
  );

  const parentDocs = useMemo(
    () => (realtimeShifts as ShiftListItem[]).map(item => ({ ...item })),
    [realtimeShifts],
  );

  const shiftsWithPolicies = useShiftPoliciesSubcollections(orgId, parentDocs);

  const shifts = useMemo(() => {
    if (!hasElasticSearchFilters) {
      return shiftsWithPolicies;
    }

    if (!searchIds?.length) {
      return [];
    }

    const byId = new Map(shiftsWithPolicies.map(item => [item.id, item] as const));

    return searchIds
      .map(id => byId.get(id))
      .filter((shift): shift is ShiftListItem => shift != null);
  }, [hasElasticSearchFilters, shiftsWithPolicies, searchIds]);

  const isFetching = isElasticResolutionPending ? true : realtimeLoading;

  const hasMoreFirestoreData = hasElasticSearchFilters
    ? Boolean(gotNewData && searchIds != null && shifts.length < searchIds.length)
    : Boolean(gotNewData);

  const paginationState = usePaginationWithFirestore(
    shifts,
    { gotNewData: hasMoreFirestoreData, loading: isFetching, next },
    SHIFT_LIST_PAGE_SIZE,
    listFilters,
    { fillIncompletePage: true, viewStorageKey: SHIFT_LIST_VIEW_STORAGE_KEY },
  );

  const error = searchError ?? realtimeError;

  // Restoring the stored page is part of the first load: the view already sits
  // on that page with no rows, so it must show loading instead of the page it is
  // about to leave (see single-loading-indicator.mdc).
  const isLoading = isFetching || paginationState.isRestoringPage;

  const paginatedData = isElasticResolutionPending
    ? []
    : (paginationState.paginatedData as ShiftListItem[]);

  return {
    filters,
    initialFilters,
    setFilters,
    toggle,
    isOpen,
    shifts,
    paginatedData,
    paginationState: {
      currentPage: paginationState.currentPage,
      pageSize: paginationState.pageSize,
      estimatedTotal: paginationState.estimatedTotal,
      totalItems: paginationState.totalItems,
      handlePageChange: paginationState.handlePageChange,
      loading: isLoading,
      hasMoreData: paginationState.hasMoreData,
    },
    loading: isLoading,
    error,
    searchTruncation,
    refetchSearchIds,
  };
};
